/**
 * Resolución pura de un nivel de la jerarquía de usuarios.
 *
 * Lógica de jerarquía (raíz efectiva / hijos por `managers` + `virtualManager`,
 * exclusión del usuario logueado y dedup).
 */

export interface HierarchyItem {
  id: string;
  managers?: string[];
  virtualManager?: string;
  email?: string;
}

export interface HierarchyOptions {
  /** Email del usuario logueado a excluir de los niveles hijos. */
  excludeEmail?: string;
}

function hasManagers(item: HierarchyItem): boolean {
  return (item.managers?.length ?? 0) > 0;
}

/**
 * Nivel raíz: **raíces efectivas** = ítems que NO son hijo de ningún padre
 * presente en `items`. Un usuario cuyo manager (o `virtualManager`) fue filtrado
 * fuera del conjunto (p. ej. al filtrar por país) se promueve a raíz, igual que
 * hace el drawer de asignación (`FilterAssign`). Así cada ítem es alcanzable por
 * drill desde alguna raíz.
 */
function getRootLevel<T extends HierarchyItem>(items: T[]): T[] {
  const idSet = new Set(items.map(item => item.id));
  const isChildOfPresent = (item: T): boolean => {
    if (item.managers?.length) {
      return item.managers.some(managerId => idSet.has(managerId));
    }
    return item.virtualManager ? idSet.has(item.virtualManager) : false;
  };
  return items.filter(item => !isChildOfPresent(item));
}

/**
 * Nivel hijo de `parentId`: reportes directos (`managers` incluye al padre) +
 * reportes virtuales (`virtualManager === padre` y sin managers reales). Excluye
 * al usuario logueado salvo que él mismo sea una raíz real.
 */
function getChildLevel<T extends HierarchyItem>(
  items: T[],
  parentId: string,
  excludeEmail?: string,
): T[] {
  const directReports = items.filter(item => item.managers?.includes(parentId));
  const virtualReports = items.filter(
    item => item.virtualManager === parentId && !hasManagers(item),
  );

  const loginIsRealRoot = items.some(
    item =>
      !!excludeEmail && item.email === excludeEmail && !item.virtualManager,
  );

  const combined = [...directReports, ...virtualReports];
  const scoped = loginIsRealRoot
    ? combined
    : combined.filter(item => item.email !== excludeEmail);

  const seen = new Set<string>();
  return scoped.filter(item => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
}

/**
 * Devuelve el nivel visible: raíz si `parentId` es null, hijos en otro caso.
 */
export function getHierarchyLevel<T extends HierarchyItem>(
  items: T[],
  parentId: string | null,
  options: HierarchyOptions = {},
): T[] {
  if (!parentId) {
    return getRootLevel(items);
  }
  return getChildLevel(items, parentId, options.excludeEmail);
}

/**
 * Usado para mostrar si es posible el drill-down. Se delega en `getChildLevel`
 * para que aparezca si el drill da ≥1 usuario (mismo criterio: managers + virtualManager,
 * exclusión del logueado, dedup) y nunca lleve a un nivel vacío.
 */
export function hasChildren<T extends HierarchyItem>(
  items: T[],
  id: string,
  options: HierarchyOptions = {},
): boolean {
  return getChildLevel(items, id, options.excludeEmail).length > 0;
}
